"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ChevronRight, Copy, Download, Loader2, Plus } from "lucide-react"
import { toast } from "sonner"
import { Blade, BladeSection, DetailField, PillButton } from "@/components/blades/blade"
import { useBlades } from "@/components/blades/blade-stack"
import { RawJsonPane, copyJson, downloadJson } from "@/components/explorer/raw-json-pane"
import { useExplorer } from "@/hooks/use-explorer"
import { useSchemaPaths } from "@/hooks/use-schema-paths"
import { copyToClipboard } from "@/lib/clipboard"
import { flattenLeaves, presentRecord, titleOf, type PresentedField } from "@/lib/record-presentation"
import { humanizeKey } from "@/lib/record-presentation"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface RecordBladeParams {
  atsSystem: string
  entityType: string
  entityDisplayName: string
  /** Null for list rows without an id (nested resources) — the seed record is shown as-is */
  id: string | null
  /** The list-row payload, shown until the full detail arrives */
  seed: Record<string, unknown> | null
  /** Position in the current page for rows without an id, so the table can highlight them */
  rowIndex: number | null
  supportsDetailFetch: boolean
}

export interface RecordBladeProps extends RecordBladeParams {
  open: boolean
  onClose: () => void
  depth?: number
  trail?: string[]
  onTrailClick?: (index: number) => void
  onAddFieldToMapping: (path: string) => void
  /** Entity names in the tenant's catalog, so embedded objects without a typed association still open */
  knownEntities?: string[]
  onAddEntityToMapping?: () => void
}

/**
 * Stable stack id for a record blade, so re-selecting the same record swaps in place. Rows
 * without an id are told apart by their position in the page, so opening a second id-less
 * row opens its own blade instead of colliding with the first.
 */
export function recordBladeId(entityType: string, id: string | null, rowIndex: number | null = null): string {
  return `record:${entityType}:${id ?? (rowIndex === null ? "row" : `row-${rowIndex}`)}`
}

/**
 * One record, readable by anyone: an Overview of labelled, grouped, formatted fields
 * (associations as chips that drill into their own blade), a flat Fields list for
 * picking mapping paths, and the Raw JSON with copy and download.
 */
export function RecordBlade({
  open,
  onClose,
  depth,
  trail,
  onTrailClick,
  atsSystem,
  entityType,
  entityDisplayName,
  id,
  seed,
  supportsDetailFetch,
  onAddFieldToMapping,
  onAddEntityToMapping,
  knownEntities,
}: RecordBladeProps) {
  const { getRawRecord } = useExplorer()
  const blades = useBlades()
  const { t, n } = useTranslation()
  const [record, setRecord] = useState<Record<string, unknown> | null>(seed)
  const [isLoading, setIsLoading] = useState(false)
  const [showEmpty, setShowEmpty] = useState(false)
  const schema = useSchemaPaths(entityType, { autoLoad: true })

  // List payloads are slim; fetch the full detail payload for inspection
  useEffect(() => {
    setRecord(seed)
    if (!supportsDetailFetch || !id) return
    let cancelled = false
    setIsLoading(true)
    getRawRecord(atsSystem, entityType, id)
      .then((detail) => {
        if (!cancelled) setRecord(detail)
      })
      .catch(() => {
        // The list row stays on screen
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem, entityType, id, seed])

  const presented = useMemo(
    () => (record ? presentRecord(record, { t, schema: schema.fields, entityDisplayName, entityNames: knownEntities }) : null),
    [record, schema.fields, entityDisplayName, knownEntities, t]
  )
  const fileStem = `${atsSystem.toLowerCase()}-${entityType.toLowerCase()}-${id ?? "record"}`

  const openAssociation = (entity: string, associatedId: string, associatedTitle: string) => {
    blades.open(
      "record",
      associatedTitle,
      {
        atsSystem,
        entityType: entity,
        entityDisplayName: humanizeKey(entity),
        id: associatedId,
        seed: null,
        rowIndex: null,
        supportsDetailFetch: true,
      } satisfies RecordBladeParams,
      { id: recordBladeId(entity, associatedId), size: "lg" }
    )
  }

  return (
    <Blade
      open={open}
      onClose={onClose}
      size="lg"
      depth={depth}
      trail={trail}
      onTrailClick={onTrailClick}
      eyebrow={entityDisplayName}
      title={presented?.title ?? (id ? `#${id}` : entityDisplayName)}
      badges={
        <>
          {presented?.status && <Pill tone="status">{presented.status}</Pill>}
          {id && <Pill>#{id}</Pill>}
          <Pill>{atsSystem}</Pill>
          {isLoading && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> {t("explorer.recordBlade.loadingFull")}
            </span>
          )}
        </>
      }
      actions={
        record ? (
          <>
            <Button variant="ghost" size="icon" className="h-8 w-8" title={t("explorer.rawJson.copyJson")} onClick={() => void copyJson(record, t)}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title={t("explorer.rawJson.downloadJson")} onClick={() => downloadJson(record, fileStem)}>
              <Download className="h-4 w-4" />
            </Button>
          </>
        ) : undefined
      }
      bare
      footer={
        onAddEntityToMapping ? (
          <>
            <span className="text-xs text-muted-foreground">{t("explorer.recordBlade.hoverHint")}</span>
            <PillButton onClick={onAddEntityToMapping}>
              <Plus className="h-3.5 w-3.5" /> {t("explorer.recordBlade.addEntityToMapping", { entity: entityDisplayName })}
            </PillButton>
          </>
        ) : undefined
      }
    >
      {!record || !presented ? (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("explorer.recordBlade.noRecord")}
        </div>
      ) : (
        <Tabs defaultValue="overview" className="flex h-full flex-col">
          <TabsList className="mx-6 mt-3 grid w-auto grid-cols-3 rounded-lg bg-muted p-0.5">
            <TabsTrigger value="overview" className="text-xs">{t("explorer.recordBlade.tabs.overview")}</TabsTrigger>
            <TabsTrigger value="fields" className="text-xs">{t("explorer.recordBlade.tabs.fields", { count: n(presented.all.length) })}</TabsTrigger>
            <TabsTrigger value="json" className="text-xs">{t("explorer.recordBlade.tabs.json")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0 px-6 pb-4">
            <div className="flex items-center justify-end gap-2 py-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={showEmpty} onCheckedChange={setShowEmpty} className="scale-90" /> {t("explorer.recordBlade.showEmpty")}
              </label>
            </div>
            {presented.groups.map((group) => (
              <BladeSection key={group.key} label={t(group.titleKey)}>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {group.fields.map((field) => (
                    <PresentedValue key={field.path} field={field} onOpenAssociation={openAssociation} onAddToMapping={onAddFieldToMapping} />
                  ))}
                </div>
              </BladeSection>
            ))}
            {showEmpty && (
              <BladeSection label={t("explorer.recordBlade.emptyGroup")}>
                <div className="flex flex-wrap gap-1.5">
                  {presented.all.filter((f) => f.kind === "empty").map((field) => (
                    <span key={field.path} className="rounded-pill border border-card-border px-2 py-0.5 text-[11px] text-muted-foreground" title={field.path}>
                      {field.label}
                    </span>
                  ))}
                </div>
              </BladeSection>
            )}
          </TabsContent>

          <TabsContent value="fields" className="mt-0 flex-1">
            <FieldList record={record} onAddToMapping={onAddFieldToMapping} />
          </TabsContent>

          <TabsContent value="json" className="mt-0 flex-1">
            <RawJsonPane data={record} fileStem={fileStem} onAddToMapping={onAddFieldToMapping} />
          </TabsContent>
        </Tabs>
      )}
    </Blade>
  )
}

function Pill({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "status" }) {
  const styles = tone === "status" ? "border-[var(--th-active-pill-border)] bg-accent text-accent-foreground" : "border-card-border bg-muted text-muted-foreground"
  return <span className={`inline-flex items-center rounded-pill border px-2 py-0.5 text-[11px] font-semibold ${styles}`}>{children}</span>
}

interface PresentedValueProps {
  field: PresentedField
  onOpenAssociation: (entityType: string, id: string, title: string) => void
  onAddToMapping: (path: string) => void
}

function PresentedValue({ field, onOpenAssociation, onAddToMapping }: PresentedValueProps) {
  const { t, n } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const actions = (
    <span className="ml-1 hidden gap-0.5 align-middle group-hover:inline-flex">
      <Button variant="ghost" size="icon" className="h-5 w-5" title={t("explorer.recordBlade.addFieldTitle", { path: field.path })} onClick={() => onAddToMapping(field.path)}>
        <Plus className="h-3 w-3" />
      </Button>
    </span>
  )

  if (field.kind === "assoc" && field.association) {
    const { entityType, id, title } = field.association
    return (
      <div className="group min-w-0">
        <div className="th-field-label truncate">{field.label}{actions}</div>
        <button
          type="button"
          disabled={!entityType}
          title={entityType ? t("explorer.recordBlade.openAssociation", { entity: entityType, id }) : `#${id}`}
          onClick={() => entityType && onOpenAssociation(entityType, id, title)}
          className="mt-0.5 inline-flex max-w-full items-center gap-1 rounded-pill border border-chip-border bg-chip px-2.5 py-0.5 text-xs font-semibold text-[var(--th-active-pill-fg)] disabled:cursor-default"
        >
          <span className="truncate">{title === `#${id}` ? `#${id}` : title}</span>
          <span className="text-[10px] font-normal text-muted-foreground">#{id}</span>
          {entityType && <ChevronRight className="h-3 w-3" />}
        </button>
      </div>
    )
  }

  if (field.kind === "list" && field.list) {
    const titles = field.list.items.map((item) => (item && typeof item === "object" ? titleOf(item as Record<string, unknown>) : String(item)))
    return (
      <div className="group col-span-full min-w-0">
        <div className="th-field-label truncate">{field.label}{actions}</div>
        <button type="button" className="mt-0.5 text-left text-[13px] hover:underline" onClick={() => setExpanded((v) => !v)}>
          {t((field.list.total === 1 ? "explorer.record.itemsOne" : "explorer.record.itemsOther"), { count: n(field.list.total ?? field.list.items.length) })}
          {field.list.items.length > 0 && (
            <span className="text-muted-foreground"> · {t((expanded ? "explorer.recordBlade.hide" : "explorer.recordBlade.show"))}</span>
          )}
        </button>
        {expanded && (
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {titles.map((title, index) => (
              <li key={index} className="rounded-pill border border-card-border px-2 py-0.5 text-xs">{title}</li>
            ))}
            {field.list.total !== null && field.list.total > field.list.items.length && (
              <li className="px-1 text-xs text-muted-foreground">{t("explorer.recordBlade.moreInJson", { count: n(field.list.total - field.list.items.length) })}</li>
            )}
          </ul>
        )}
      </div>
    )
  }

  const multiline = field.kind === "html" || field.display.length > 80
  return (
    <div className={`group min-w-0 ${multiline ? "col-span-full" : ""}`}>
      <DetailField label={<>{field.label}{actions}</>} title={field.detail ?? field.path} mono={field.kind === "number" || field.kind === "money"}>
        <span className={multiline ? "whitespace-pre-wrap" : ""}>{field.display}</span>
      </DetailField>
    </div>
  )
}

function FieldList({ record, onAddToMapping }: { record: Record<string, unknown>; onAddToMapping: (path: string) => void }) {
  const { t, n } = useTranslation()
  const [query, setQuery] = useState("")
  const leaves = useMemo(() => flattenLeaves(record, t), [record, t])
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase()
    return term ? leaves.filter((leaf) => leaf.path.toLowerCase().includes(term) || leaf.display.toLowerCase().includes(term)) : leaves
  }, [leaves, query])

  const copyPath = async (path: string) => {
    if (await copyToClipboard(path)) toast.success(t("explorer.common.copiedPath", { path }))
    else toast.error(t("explorer.recordBlade.clipboardError"), { description: path })
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-card-border px-6 py-2">
        <Input className="h-8 text-xs" placeholder={t("explorer.recordBlade.searchFields", { count: n(leaves.length) })} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <ul className="divide-y divide-[var(--th-menu-border)] px-2">
        {visible.map((leaf) => (
          <li key={leaf.path} className="group grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-3 px-4 py-1.5 hover:bg-row-hover">
            <span className="truncate font-mono text-[11px] text-foreground" title={leaf.path}>{leaf.path}</span>
            <span className="truncate text-xs text-muted-foreground" title={leaf.display}>{leaf.display || <em>{t("explorer.common.empty")}</em>}</span>
            <span className="invisible flex gap-0.5 group-hover:visible">
              <Button variant="ghost" size="icon" className="h-6 w-6" title={t("explorer.recordBlade.copyPath")} onClick={() => void copyPath(leaf.path)}>
                <Copy className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" title={t("explorer.recordBlade.addToMapping")} onClick={() => onAddToMapping(leaf.path)}>
                <Plus className="h-3 w-3" />
              </Button>
            </span>
          </li>
        ))}
        {visible.length === 0 && <li className="px-4 py-6 text-center text-xs text-muted-foreground">{t("explorer.recordBlade.noMatchingFields")}</li>}
      </ul>
    </div>
  )
}
